import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useCurrency } from '../../contexts/CurrencyContext';

interface CurrencyDisplayProps {
  showExample?: boolean;
  exampleAmount?: number;
}

const CurrencyDisplay: React.FC<CurrencyDisplayProps> = ({
  showExample = true,
  exampleAmount = 1234.56
}) => {
  const theme = useTheme();
  const { 
    currency, 
    loading, 
    formatCurrency, 
    formatCurrencyWithCommas,
    getCurrencySymbol,
    getCurrencyCode,
    getCurrencyLabel
  } = useCurrency();

  if (loading) {
    return (
      <Paper
        sx={{
          p: 2,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      >
        <Typography variant="body2" color="text.secondary">
          جاري تحميل إعدادات العملة...
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.15)}`
        }
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.success.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.success.main
          }}
        >
          <MoneyIcon />
        </Box>
        
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            العملة الحالية
          </Typography>
          <Typography variant="body2" color="text.secondary">
            العملة المستخدمة في جميع أنحاء النظام
          </Typography>
        </Box>

        <Chip
          label="نشط"
          color="success"
          size="small"
          icon={<TrendingUpIcon />}
          variant="outlined"
        />
      </Box>

      {/* Currency Info */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h4" fontWeight="bold" color="success.main">
            {getCurrencySymbol()}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {getCurrencyLabel()}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary">
          كود العملة: <strong>{getCurrencyCode()}</strong>
        </Typography>
      </Box>

      {/* Example */}
      {showExample && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            مثال على التنسيق:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                تنسيق عادي:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatCurrency(exampleAmount)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                مع فواصل الآلاف:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatCurrencyWithCommas(exampleAmount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Note */}
      <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
        <Typography variant="caption" color="text.secondary">
          💡 يمكنك تغيير العملة من صفحة الإعدادات العامة
        </Typography>
      </Box>
    </Paper>
  );
};

export default CurrencyDisplay;
